/**
 * <ee-phone-preview> — iPhone 15 Pro frame with a live TenantClient preview.
 *
 * Renders an iframe inside a CSS-only phone chassis.  The iframe points at the
 * TenantClient application in preview mode (`?preview=true`).
 *
 * Attributes:
 *   src       — Full URL to the TenantClient page (e.g., "http://localhost:5179/demo/schedule").
 *               `?preview=true` is appended automatically.
 *   label     — Caption displayed beneath the phone frame.
 *   darkmode  — Boolean attribute. When present, `&darkmode=true` is added to the iframe URL.
 *   nocallout — Boolean attribute. When present, hides the best-effort disclaimer text.
 *
 * Methods:
 *   setTheme({ primary, accent, darkPrimary, darkAccent })
 *     Sends a `postMessage` to the iframe so the preview instantly applies the
 *     supplied colors without a full reload.
 */

export class PhonePreviewElement extends HTMLElement {
  private _iframe!: HTMLIFrameElement;
  private _label!: HTMLDivElement;
  private _callout!: HTMLDivElement;
  private _actionsEl!: HTMLDivElement;

  static get observedAttributes() {
    return ["src", "label", "darkmode", "nocallout"];
  }

  connectedCallback() {
    this._build();
    this._updateSrc();
  }

  attributeChangedCallback(name: string, oldVal: string | null, newVal: string | null) {
    if (!this._iframe) return;
    if (name === "src" || name === "darkmode") this._updateSrc();
    if (name === "label") this._label.textContent = newVal || "Client Preview";
    if (name === "nocallout") this._callout.style.display = this.hasAttribute("nocallout") ? "none" : "";
  }

  // ── Public API ──────────────────────────────────────

  /**
   * Send theme colors to the iframe via postMessage.
   * The TenantClient preview-mode listener applies these as CSS custom
   * properties on the `.app-shell` element.
   */
  setTheme(colors: {
    primary?: string;
    accent?: string;
    darkPrimary?: string;
    darkAccent?: string;
  }) {
    this._iframe?.contentWindow?.postMessage(
      { type: "ee-set-theme", ...colors },
      "*"
    );
  }

  // ── DOM ─────────────────────────────────────────────

  private _build() {
    // Phone frame
    const container = document.createElement("div");
    container.className = "phone-preview-container";

    const frame = document.createElement("div");
    frame.className = "phone-frame";

    const screen = document.createElement("div");
    screen.className = "phone-screen";

    this._iframe = document.createElement("iframe");
    this._iframe.title = this.getAttribute("label") || "Client Preview";
    this._iframe.loading = "lazy";
    this._iframe.sandbox.add("allow-scripts", "allow-same-origin", "allow-forms", "allow-popups");

    screen.appendChild(this._iframe);
    frame.appendChild(screen);
    container.appendChild(frame);

    // Label
    this._label = document.createElement("div");
    this._label.className = "phone-preview-label";
    this._label.textContent = this.getAttribute("label") || "Client Preview";
    container.appendChild(this._label);

    // Actions
    this._actionsEl = document.createElement("div");
    this._actionsEl.className = "phone-preview-actions";

    const refreshBtn = document.createElement("button");
    refreshBtn.type = "button";
    refreshBtn.className = "btn btn-sm btn-outline-secondary";
    refreshBtn.title = "Refresh preview";
    refreshBtn.innerHTML = '<i class="bi bi-arrow-clockwise"></i> Refresh';
    refreshBtn.addEventListener("click", () => this._refresh());

    const openBtn = document.createElement("a");
    openBtn.className = "btn btn-sm btn-outline-secondary";
    openBtn.title = "Open in new tab";
    openBtn.target = "_blank";
    openBtn.rel = "noopener";
    openBtn.innerHTML = '<i class="bi bi-box-arrow-up-right"></i> Open';
    // href synced in _updateSrc

    this._actionsEl.append(refreshBtn, openBtn);
    container.appendChild(this._actionsEl);

    // Callout
    this._callout = document.createElement("div");
    this._callout.className = "phone-preview-callout";
    this._callout.innerHTML =
      '<i class="bi bi-info-circle"></i> ' +
      "This is a best-effort estimate of what the current page will look like. " +
      "It may not be exact if you navigate in the app. Check your current page by pressing <strong>Refresh</strong>.";
    if (this.hasAttribute("nocallout")) this._callout.style.display = "none";
    container.appendChild(this._callout);

    this.appendChild(container);
  }

  // ── Helpers ─────────────────────────────────────────

  private _buildUrl(): string {
    const raw = this.getAttribute("src") || "/";
    const sep = raw.includes("?") ? "&" : "?";
    const dark = this.hasAttribute("darkmode") ? "&darkmode=true" : "";
    return `${raw}${sep}preview=true${dark}`;
  }

  private _updateSrc() {
    const url = this._buildUrl();
    this._iframe.src = url;

    // Keep the "Open" link in sync
    const openLink = this._actionsEl?.querySelector("a") as HTMLAnchorElement | null;
    if (openLink) openLink.href = url;
  }

  private _refresh() {
    // Force a reload by re-assigning the same src
    this._iframe.src = this._iframe.src;
  }
}
